/**
 * TickerFeed Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A professional animated ticker feed component for displaying scrolling content.
 * Built with react-slick for smooth, customizable animations and responsive behavior.
 * 
 * CORE FEATURES:
 * - Smooth horizontal scrolling animation with customizable speed
 * - Configurable infinite loop behavior for continuous content flow
 * - Autoplay functionality with pause-on-hover capabilities
 * - Variable width support for items of different sizes
 * - Touch/swipe interaction control for mobile devices
 * - CSS-based linear easing for professional smooth motion
 * - Responsive design with flexible container sizing
 * 
 * ANIMATION SYSTEM:
 * - Linear CSS easing for consistent scroll speed
 * - Configurable animation speed (default: 10000ms)
 * - Pause-on-hover functionality for user interaction
 * - Infinite loop support for continuous content display
 * - Variable width items accommodate different content lengths
 * 
 * CUSTOMIZATION OPTIONS:
 * - Speed control for animation timing
 * - Infinite loop toggle for content cycling
 * - Autoplay control with manual override
 * - Hover pause behavior for user experience
 * - Swipe/touch interaction configuration
 * 
 * TECHNICAL IMPLEMENTATION:
 * - React-slick integration for professional carousel behavior
 * - State management for dynamic content updates
 * - Effect hooks for data synchronization
 * - Custom CSS styling with professional animations
 * - Production-ready with comprehensive prop validation
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of ticker item objects
 * @param {Object} props.data[].text - Display text for ticker item
 * @param {string} props.data[].color - CSS color for ticker item text
 * @param {boolean} props.infinite - Enable infinite loop scrolling (default: true)
 * @param {number} props.speed - Animation speed in milliseconds (default: 10000)
 * @param {boolean} props.autoplay - Enable automatic scrolling (default: true)
 * @param {boolean} props.pauseOnHover - Pause animation on hover (default: false)
 * @param {boolean} props.swipe - Enable touch/swipe interactions (default: false)
 * @returns {JSX.Element} Animated ticker feed component
 */
import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Ticker.css'; 

/**
 * TickerFeed Component
 * 
 * Professional animated ticker component with customizable behavior and styling.
 * Integrates with react-slick for smooth scrolling animations.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Ticker content data array
 * @param {boolean} props.infinite - Infinite loop behavior
 * @param {number} props.speed - Animation speed
 * @param {boolean} props.autoplay - Autoplay functionality
 * @param {boolean} props.pauseOnHover - Hover pause behavior
 * @param {boolean} props.swipe - Touch interaction support
 * @returns {JSX.Element} Ticker feed interface
 */
function TickerFeed({data, infinite=true, speed=10000, autoplay=true, pauseOnHover=false, swipe=false}) {
  const [tickerItems, setTickerItems] = useState([]);

  useEffect(() => {
    if (data) {
      setTickerItems(data);
    }
  }, [data]);

  // Slider settings for the ticker effect
  const settings = {
    dots: false,
    infinite: infinite,
    speed: speed, 
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: autoplay,
    autoplaySpeed: 0,
    cssEase: 'linear',
    variableWidth: true, 
    arrows: false,
    pauseOnHover: pauseOnHover,
    swipe: swipe, 
  };

  return (
    <div className="ticker-feed-container">
      <Slider {...settings}>
        {tickerItems.map((item, index) => (
          <div key={index} className="ticker-item">
            <span style={{ color: item.color }}>{item.text}</span>
          </div>
        ))}
      </Slider>
    </div>
  );
}

export default TickerFeed;
